import { startCase } from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable/index";
import {
  clearError,
  deleteNOCExIndiaRequest,
  fetchNOCExIndiaRequests,
  NOCExIndiaRequest,
  setCurrentRequest,
} from "../../../core/data/redux/nocExIndiaSlice";
import DeleteModal from "../../../core/modals/deleteModal";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { all_routes } from "../../router/all_routes";
import SyncNocRequest from "./sync-noc-request";
import { DatePicker } from "antd";
import { ConfigProvider } from "antd";
import dayjs from "dayjs";
import RequestStatusBadge from "./RequestStatusBadge";
import DateRangeFilter from "../../../core/common/DateRangeFilter";
import RequestStatusFilter from "./RequestStatusFilter";
import { isAdminOrSubAdmin, isSupportViewer } from "../../../utils/permissionUtils";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { processNocDownloadResponse } from "../../../utils/nocDownloadUtils";

interface NOCForeignVisitProps { }

const user: any = localStorage.getItem("user");
const userJson = user && user != "undefined" ? JSON.parse(user) : {};
let userType: any = userJson?.user_type || userJson?.role;
const isManagement = isAdminOrSubAdmin(userType) || isSupportViewer(userType);

// --- Utility Functions (Standardized) ---

// ⚡ Consistent Date Format: DD-MM-YYYY
const formatDate = (dateString: string | null) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return "-";
  }
};

const formatExIndiaDnarStatus = (val?: string) => {
  if (!val) return "-";
  const trimmed = String(val).trim().toUpperCase();
  if (trimmed === "N" || trimmed === "NO" || trimmed === "NO D&AR CASE" || trimmed.startsWith("NO D&AR")) {
    return "No D&AR Case";
  }
  if (trimmed === "C" || trimmed === "COMPLETED") {
    return "D&AR completed";
  }
  if (trimmed === "P" || trimmed === "PENDING") {
    return "D&AR pending";
  }
  return val;
};

// --- Component ---

const NOCForeignVisit = (props: NOCForeignVisitProps) => {
  const dispatch = useDispatch<any>();
  const routes = all_routes;

  // Redux state
  const { requests, loading, error, pagination, deleteLoading } = useSelector(
    (state: any) => state.nocExIndiaState
  );

  // Local state
  const [deleteModalId, setDeleteModalId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterEmpId, setFilterEmpId] = useState<string>("");
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(null);
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [downloadingRecordId, setDownloadingRecordId] = useState<number | null>(null);

  // Data fetching logic
  const fetchData = (
    page = pagination.page,
    limit = pagination.limit,
    query = searchQuery,
    requestStatus = selectedStatus,
    dateStart = startDate,
    dateEnd = endDate
  ) => {
    const params: any = {
      page,
      limit,
      query,
      orderColumn: "created_at",
      orderBy: "DESC",
    };
    if (requestStatus) params.requestStatus = requestStatus;
    if (dateStart) params.startDate = dateStart;
    if (dateEnd) params.endDate = dateEnd;
    dispatch(fetchNOCExIndiaRequests(params));
  };

  // Fetch data on component mount/pagination change
  useEffect(() => {
    fetchData();
  }, [pagination.page, pagination.limit]);

  // ⚡ New: Toast for Redux errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Handle pagination change
  const handlePagination = (newPagination: any, filters: any, sorter: any) => {
    const page = newPagination.current || 1;
    const pageSize = newPagination.pageSize || pagination.limit;
    fetchData(page, pageSize, searchQuery, selectedStatus, startDate, endDate);
  };

  // Handle search action
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    fetchData(1, pagination.limit, query, selectedStatus, startDate, endDate); // ⚡ Resets to page 1 on new search
  };

  const handleApplyFilter = () => {
    fetchData(1, pagination.limit, searchQuery, selectedStatus, startDate, endDate);
  };

  const handleClearFilter = () => {
    setSelectedStatus("");
    setStartDate("");
    setEndDate("");
    fetchData(1, pagination.limit, searchQuery, "", "", "");
  };

  // Handle delete confirmation
  const handleDeleteClick = (id: number) => {
    setDeleteModalId(id);
  };

  // Handle delete confirmation response
  const handleDeleteConfirm = async (response: string) => {
    if (response === "YES" && deleteModalId) {
      try {
        await dispatch(deleteNOCExIndiaRequest({ id: deleteModalId })).unwrap();
        toast.success("NOC request deleted successfully!");
        fetchData(pagination.page, pagination.limit, searchQuery, selectedStatus, startDate, endDate); // Re-fetch current page
      } catch (error) {
        console.error("Failed to delete request:", error);
        toast.error("Failed to delete NOC request. Please try again.");
      }
    }
    setDeleteModalId(null);
  };

  // Handle view details click
  const handleViewDetailsClick = (record: NOCExIndiaRequest) => {
    dispatch(setCurrentRequest(record.id));
  };

  // Handle edit button click
  const handleEditClick = (record: NOCExIndiaRequest) => {
    dispatch(setCurrentRequest(record.id));
  };

  // Columns definition matching SAP NOC for Ex-India Leave Previous Requests
  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 70,
      render: (_: any, __: any, index: number) => (
        <div>
          {pagination.page
            ? index + pagination.page * pagination.limit - pagination.limit + 1
            : index + 1}
        </div>
      ),
    },
    {
      title: "Reference Number",
      dataIndex: "referenceNumber",
      sorter: (a: NOCExIndiaRequest, b: NOCExIndiaRequest) =>
        (a.referenceNumber || "").localeCompare(b.referenceNumber || ""),
      render: (text: string) => (
        <span className="fw-medium font-monospace">{text || "-"}</span>
      ),
    },
    {
      title: "Place of visit",
      dataIndex: "placeOfVisitNoc",
      sorter: (a: NOCExIndiaRequest, b: NOCExIndiaRequest) =>
        (a.placeOfVisitNoc || a.placeOfVisit || "").localeCompare(b.placeOfVisitNoc || b.placeOfVisit || ""),
      render: (text: string, record: any) => text || record.placeOfVisit || record.ePlaceOfVisitNoc || "-",
    },
    {
      title: "period of visit from",
      dataIndex: "periodOfVisitFrom",
      sorter: (a: NOCExIndiaRequest, b: NOCExIndiaRequest) =>
        new Date(a.periodOfVisitFrom || 0).getTime() - new Date(b.periodOfVisitFrom || 0).getTime(),
      render: (text: string, record: any) => formatDate(text || record.ePeriodOfVisitFrom),
    },
    {
      title: "period of visit to",
      dataIndex: "periodOfVisitTo",
      sorter: (a: NOCExIndiaRequest, b: NOCExIndiaRequest) =>
        new Date(a.periodOfVisitTo || 0).getTime() - new Date(b.periodOfVisitTo || 0).getTime(),
      render: (text: string, record: any) => formatDate(text || record.ePeriodOfVisitTo),
    },
    {
      title: "Leave Type",
      dataIndex: "leaveType",
      render: (text: string, record: any) => text || record.leaveDetails || record.eLeaveDetails || "-",
    },
    {
      title: "Total expenditure",
      dataIndex: "totalExpenditure",
      sorter: (a: NOCExIndiaRequest, b: NOCExIndiaRequest) =>
        Number(a.totalExpenditure || 0) - Number(b.totalExpenditure || 0),
      render: (text: string | number) => {
        if (text === undefined || text === null || text === "") return "-";
        const num = Number(text);
        if (isNaN(num)) return String(text);
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
    {
      title: "D&AR Status",
      dataIndex: "dnARStatus",
      sorter: (a: NOCExIndiaRequest, b: NOCExIndiaRequest) =>
        (a.dnARStatus || "").localeCompare(b.dnARStatus || ""),
      render: (text: string, record: any) =>
        formatExIndiaDnarStatus(text || record.dsarStatus || record.dandARStatus || record.dnARPending),
    },
    {
      title: "(if penalty) Effective From",
      dataIndex: "dnARPenaltyEffecti",
      render: (text: string, record: any) => formatDate(text || record.penaltyEffectiveFrom),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      render: (text: string) => (
        <div
          className="text-truncate"
          style={{ maxWidth: "200px" }}
          title={text || "-"}
        >
          {text || "-"}
        </div>
      ),
    },
    {
      title: "Request Creation Date",
      dataIndex: "requestCreationDate",
      sorter: (a: NOCExIndiaRequest, b: NOCExIndiaRequest) =>
        new Date(a.requestCreationDate || a.created_at || 0).getTime() -
        new Date(b.requestCreationDate || b.created_at || 0).getTime(),
      render: (text: string, record: any) =>
        formatDate(text || record.created_at || record.sapCreatedAt),
    },
    {
      title: "Request Status",
      dataIndex: "requestStatus",
      render: (status: string, record: any) => (
        <RequestStatusBadge status={status || record.status} />
      ),
    },
    {
      title: "Last/Latest Approver",
      dataIndex: "lastLatestApprover",
      sorter: (a: any, b: any) =>
        (a.lastLatestApprover || a.approverName || "").localeCompare(b.lastLatestApprover || b.approverName || ""),
      render: (text: string, record: any) => {
        const name = text || record.approverName || record.lastApprover || record.approver?.name;
        const id = record.approverUserID || record.approverId || record.approver?.empId;
        if (name && id) return `${name} (${formatEmpIdForDisplay(id)})`;
        if (name) return name;
        if (id) return formatEmpIdForDisplay(id);
        return "-";
      },
    },
    {
      title: "Remarks (if rejected)",
      dataIndex: "hRemarks",
      render: (text: string, record: any) => {
        const val = text || record.remarksIfRejected || record.HRemarks || record.remarks1 || record.Remarks1;
        return val || "-";
      },
    },
  ];

  // Always include Actions column so users can view/edit draft requests
  columns.push({
    title: "Actions",
    dataIndex: "actions",
    render: (text: string, record: NOCExIndiaRequest) => {
      const isDraft = Boolean(
        record.is_draft ||
        (record as any).isDraft ||
        record.requestStatus === "DRAFT" ||
        record.requestStatus === "draft" ||
        record.requestStatus === "-" ||
        !record.requestStatus
      );

      const isCompleted = String(record?.requestStatus || "").toUpperCase() === "D";

      const handleDownloadNocDoc = async (rec: any) => {
        if (downloadingRecordId === rec.id) return;
        try {
          setDownloadingRecordId(rec.id);
          const payload = {
            empId: rec.empID || rec.empId,
            type: "ZNEX",
            referenceNumber: rec.referenceNumber,
          };
          const res = await Api.post(API_URLS.NOC.EX_INDIA_DOWNLOAD, payload);
          processNocDownloadResponse(res?.data);
        } catch (err: any) {
          toast.error(err?.message || "Failed to download NOC document.");
        } finally {
          setDownloadingRecordId(null);
        }
      };

      const isDownloading = downloadingRecordId === record.id;

      return (
        <div className="action-icon d-inline-flex align-items-center">
          <Link
            to={`${routes.noc.foreignVisit}/details/${record.id}`}
            className="text-primary me-2"
            title="View Details"
            onClick={() => handleViewDetailsClick(record)}
          >
            <i className="ti ti-eye fs-16"></i>
          </Link>
          {isCompleted && (
            <button
              type="button"
              className="btn btn-link text-info p-0 me-2"
              title="Download Digital NOC"
              onClick={() => handleDownloadNocDoc(record)}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <i className="ti ti-file-text fs-16"></i>
              )}
            </button>
          )}
          {isDraft && (
            <>
              <Link
                to={`${routes.noc.foreignVisit}/edit/${record.id}`}
                className="text-success me-2"
                title="Edit Request"
                onClick={() => handleEditClick(record)}
              >
                <i className="ti ti-edit fs-16"></i>
              </Link>
              <button
                type="button"
                className="btn btn-link text-danger p-0"
                title="Delete Request"
                onClick={() => handleDeleteClick(record.id)}
                disabled={deleteLoading}
                data-bs-toggle="modal"
                data-bs-target="#delete_modal"
              >
                <i className="ti ti-trash fs-16"></i>
              </button>
            </>
          )}
        </div>
      );
    },
  });

  // Clear error on component unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const user: any = localStorage.getItem("user");
  const userJson = user && user !== "undefined" ? JSON.parse(user) : {};
  const currentUserType: any = userJson?.user_type || userJson?.role;
  const isEmployee = !currentUserType || currentUserType.toUpperCase() === "EMPLOYEE";

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              NOC for Ex-India (Foreign Visit)
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Foreign Visit
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card">
          {/* Card Header: Title on Left, Action Buttons on Right */}
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3 py-3">
            <h4 className="mb-0 fw-bold">NOC Applications</h4>
            <div className="d-flex align-items-center gap-2">
              {!isManagement && (
                <Link
                  to={`${routes.noc.foreignVisit}/create`}
                  className="btn btn-primary text-nowrap"
                >
                  <i className="ti ti-plus me-1"></i>
                  Apply NOC Ex-India
                </Link>
              )}
              <SyncNocRequest nocType={'EX-INDIA'} onSyncSuccess={() => fetchData()} />
            </div>
          </div>

          <div className="card-body">
            {/* Dedicated Modern Filter Toolbar */}
            <div className="p-3 bg-light rounded-3 border mb-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <RequestStatusFilter
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                  />
                  <DateRangeFilter
                    startDate={startDate}
                    endDate={endDate}
                    onChangeStart={setStartDate}
                    onChangeEnd={setEndDate}
                    onApply={handleApplyFilter}
                    onClear={handleClearFilter}
                    variant="inline"
                  />
                </div>
              </div>
            </div>
            <Table
              columns={columns}
              dataSource={requests}
              Selection={false}
              paginationConfig={{
                current: pagination.page,
                pageSize: pagination.limit,
                total: pagination.total || 0,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showTotal: (total: number, range: [number, number]) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              handlePagination={handlePagination}
              loading={loading}
              handleSearch={handleSearch}
            />
          </div>
        </div>

        {/* Delete Modal */}
        <DeleteModal handleDelete={handleDeleteConfirm} />
      </div>
    </div>
  );
};

export default NOCForeignVisit;
